/**
 * Camada 1 · campo de ESCOLHA entre opcoes fechadas.
 *
 * ELE DEIXOU DE USAR `<select>` NATIVO EM 06/09/2026, pelo ADR 0053, e o motivo esta
 * registrado la: a roda que o nativo abre no celular segue o tema do SISTEMA OPERACIONAL,
 * e nao o do app. Com o app em escuro e o telefone em claro, ela abre BRANCA no meio de
 * uma tela preta.
 *
 * O ARGUMENTO ANTIGO ERA BOM, E CONTINUA VERDADEIRO: a roda do iOS e um gesto que a pessoa
 * ja conhece, funciona com leitor de tela sem uma linha de `aria`, e nao pede tratamento de
 * foco. Abrir mao dela custa caro, e este arquivo so se paga se devolver as quatro coisas
 * que ela dava de graca:
 *
 *   1. ALVO DE 44px em cada opcao, e nao uma lista apertada.
 *   2. A OPCAO SELECIONADA VISIVEL ao abrir, e nao a lista no topo.
 *   3. A LISTA ABRE PARA CIMA quando nao ha espaco embaixo, para o teclado do celular nao
 *      cobrir justamente o que a pessoa precisa ler.
 *   4. FECHAR POR GESTO: Esc, clique fora, e o foco voltando para quem abriu.
 *
 * A API PUBLICA NAO MUDOU, e isso e deliberado: `rotulo`, `opcoes`, `obrigatorio`, `erro`,
 * `ajuda` e `vazio` continuam iguais. E o que permite trocar a implementacao em TODOS os
 * produtos sem tocar numa linha de tela, que e a razao de a peca existir.
 *
 * O ROTULO E OBRIGATORIO NO TIPO, como no `Campo`: escolha sem nome e adivinhacao, e quem
 * usa leitor de tela ouviria so os valores.
 */

import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { t } from '@complexity/i18n';
import { IconeChevron, IconeVisto } from './Icones.js';

export interface OpcaoDeSelecao {
  readonly valor: string;
  readonly rotulo: string;
}

export interface SelecaoProps {
  readonly rotulo: string;
  readonly opcoes: readonly OpcaoDeSelecao[];
  readonly value?: string;
  readonly onChange?: (evento: { target: { value: string; name?: string } }) => void;
  readonly name?: string;
  readonly disabled?: boolean;
  /** Marca visual, atributo required e anuncio ao leitor de tela, os tres de uma vez. */
  readonly obrigatorio?: boolean;
  readonly erro?: string;
  readonly ajuda?: string;
  /**
   * O texto da opcao vazia, quando existe uma.
   *
   * AUSENTE, NAO HA OPCAO VAZIA, e e o padrao certo: um campo que nasce com "Selecione..."
   * e obrigatorio obriga a pessoa a um gesto a mais para escolher o que ja estava certo. A
   * opcao vazia so faz sentido quando NAO escolher e uma resposta valida.
   */
  readonly vazio?: string;
  /**
   * A LISTA GANHA UM CAMPO DE BUSCA quando aberta.
   *
   * PEDIDO DO FUNDADOR EM 13/09/2026, sobre o campo de UF do conselho: "UF do conselho
   * devera ter combo com pesquisa". Sao 27 opcoes, e o Material 3 e o Primer recomendam
   * busca a partir de mais ou menos dez: acima disso a pessoa le a lista inteira para achar
   * o que ja sabia.
   *
   * OPCIONAL E DESLIGADA POR PADRAO, e nao ligada automaticamente pelo tamanho da lista.
   * Ligar por contagem mudaria, de uma vez, toda tela desta plataforma que tem lista longa,
   * sem ninguem ter decidido isso. Quem quer, declara.
   *
   * SEM ELA, A PECA NAO MUDA EM NADA: o mesmo desenho, o mesmo teclado, e a mesma letra que
   * pula para a opcao.
   */
  readonly comBusca?: boolean;
}

export function Selecao({
  rotulo,
  opcoes,
  value,
  onChange,
  name,
  disabled,
  obrigatorio,
  erro,
  ajuda,
  vazio,
  comBusca,
}: SelecaoProps) {
  const id = useId();
  const idLista = `${id}-lista`;
  const idAjuda = `${id}-ajuda`;
  const idErro = `${id}-erro`;

  const [aberto, setAberto] = useState(false);
  // O item sob o cursor de TECLADO, que e diferente do escolhido: navegar com as setas
  // move este, e so o Enter transforma um no outro. Sem essa separacao, cada seta
  // dispararia uma mudanca de valor, e um formulario com validacao acusaria erro a cada
  // tecla enquanto a pessoa ainda esta procurando.
  const [ativo, setAtivo] = useState(-1);
  // Para cima ou para baixo. Medido no clique, e nao presumido: em tela de celular com o
  // teclado aberto, o espaco util muda depois que o campo recebe foco.
  const [paraCima, setParaCima] = useState(false);
  /**
   * O teto de altura da lista, em pixels, medido no momento de abrir. 280 é o valor de antes, e
   * vale enquanto houver espaço; ver o comentário em `abrir()`.
   */
  const [alturaMaxima, setAlturaMaxima] = useState(280);
  /**
   * HÁ MAIS OPÇÃO ABAIXO DO QUE SE VÊ? É o que acende o degradê no rodapé da lista.
   *
   * POR QUE ELE EXISTE, medido em 07/10/2026 numa tela de 390px: com doze opções, seis apareciam
   * inteiras e a barra de rolagem tinha largura zero. A lista rolava, e nada na tela dizia isso.
   * O degradê é o sinal que fica parado, e some quando a pessoa chega ao fim: sinal que não some
   * vira enfeite, e aí ninguém mais o lê.
   */
  const [haMais, setHaMais] = useState(false);
  /**
   * Quantas opções estão desenhadas agora. O efeito que mede o "tem mais" depende dela, e ela é
   * gravada mais abaixo, quando a lista visível é montada: o efeito precisa ser declarado antes de
   * `itens` existir, e um estado é o que atravessa essa ordem sem reordenar o arquivo inteiro.
   */
  const [quantidadeVisivel, setQuantidadeVisivel] = useState(0);

  /**
   * A MEDIÇÃO ACONTECE DEPOIS DO DESENHO, e não logo após o clique.
   *
   * MEDIDO em 07/10/2026: com `requestAnimationFrame` logo depois de abrir, a lista às vezes ainda
   * não existia quando a medição rodava, e o degradê não aparecia. Em 390px passava e em 1280px
   * falhava, que é a assinatura de corrida de tempo, e não de regra errada. O efeito roda depois
   * de o React desenhar, então a lista está lá sempre.
   *
   * As duas dependências são as que mudam o tamanho do conteúdo: abrir, e filtrar pela busca.
   */
  useEffect(() => {
    if (aberto) medirSeHaMais();
    // `medirSeHaMais` fica FORA das dependências de propósito: ela só lê a referência da lista e
    // grava o estado, e entrar aqui faria o efeito rodar a cada render sem medir nada de novo.
    // `quantidadeVisivel` acompanha a busca, que é o outro jeito de o conteúdo mudar de tamanho.
  }, [aberto, quantidadeVisivel]);

  /** Mede se ainda sobra conteúdo abaixo. Chamada ao abrir, ao rolar e quando a busca filtra. */
  function medirSeHaMais() {
    const el = lista.current;
    if (!el) return;
    // 2px de folga: arredondamento de subpixel faria o degradê piscar no fim da rolagem.
    setHaMais(el.scrollTop + el.clientHeight < el.scrollHeight - 2);
  }
  /**
   * ONDE O PAINEL E DESENHADO NA JANELA, em coordenadas de viewport.
   *
   * ---------------------------------------------------------------------------
   * O DEFEITO QUE ISTO CONSERTA, medido na tela em 14/09/2026
   * ---------------------------------------------------------------------------
   * O fundador abriu o combo de forma de tratamento e viu SO a primeira opcao: *"hoje so
   * tem Dr."*. As quatro estavam no DOM e desenhadas, medidas em y=477, 521 e 565. O que
   * acontecia e que o CARTAO as cortava: ele tem `overflow-hidden` para o conteudo
   * respeitar o canto arredondado, e o painel ia ate 609 enquanto o cartao acabava em 525.
   * 84 pixels de lista, invisiveis.
   *
   * ABRIR PARA CIMA NAO RESOLVERIA: o corte e do ANCESTRAL, e para cima ele corta igual.
   *
   * ENTAO O PAINEL SAI DA ARVORE e vai para o `body`, com posicao `fixed` calculada do
   * gatilho. Fora da arvore, nenhum `overflow` de ancestral o alcanca. E o que toda
   * plataforma grande faz com menu suspenso, e pela mesma razao.
   */
  const [caixaDoGatilho, setCaixaDoGatilho] = useState<{
    readonly top: number;
    readonly left: number;
    readonly width: number;
    readonly bottom: number;
  } | null>(null);
  /** O painel, para o clique fora saber que ele TAMBEM e "dentro". Ver o efeito adiante. */
  const painel = useRef<HTMLDivElement>(null);

  /**
   * O TEXTO DIGITADO NA BUSCA, e ele so existe quando `comBusca` esta ligada.
   *
   * ELE SE LIMPA AO FECHAR, e nao ao escolher: guardar o filtro entre uma abertura e outra
   * faria a lista abrir ja recortada da vez anterior, e a pessoa concluiria que as outras
   * opcoes sumiram.
   */
  const [busca, setBusca] = useState('');

  const raiz = useRef<HTMLDivElement>(null);
  const gatilho = useRef<HTMLButtonElement>(null);
  const lista = useRef<HTMLUListElement>(null);
  const campoBusca = useRef<HTMLInputElement>(null);

  // A lista com a opcao vazia na frente, quando ela existe. Montada aqui e nao no render
  // para o indice do teclado e o do desenho serem o MESMO: dois indices diferentes para a
  // mesma lista e a origem classica do "a seta seleciona o item de cima".
  /**
   * COMPARACAO SEM ACENTO E SEM CAIXA, e isto ja custou caro numa peca irma.
   *
   * Quem digita `sao` esperando `São Paulo` nao acha nada se a comparacao for literal, e a
   * conclusao dela e que a opcao NAO EXISTE. Numa lista de UFs isso e so irritante; na
   * `SelecaoMultipla` de especialidades produziu duplicata cadastrada a mao, e por isso a
   * mesma normalizacao esta nas duas.
   */
  const normalizar = (texto: string) =>
    texto
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

  /**
   * A LISTA DESENHADA, ja com a opcao vazia e ja filtrada.
   *
   * A OPCAO VAZIA SAI ASSIM QUE ALGUEM DIGITA, e isso e deliberado: "Selecione" no meio de
   * um resultado de busca e ruido, e pior, pode ficar como unica linha visivel e parecer um
   * resultado. Sem busca ativa ela volta ao topo, como sempre esteve.
   *
   * O FILTRO CASA PELA SIGLA E PELO NOME ao mesmo tempo, porque o rotulo carrega os dois
   * (`SP · São Paulo`). Quem sabe a sigla digita duas letras e chega; quem nao sabe digita
   * o nome. `includes` e nao `startsWith` justamente por isso: o nome vem depois da sigla.
   */
  const alvoDaBusca = comBusca ? normalizar(busca) : '';
  const comVazio: readonly OpcaoDeSelecao[] = vazio
    ? [{ valor: '', rotulo: vazio }, ...opcoes]
    : opcoes;
  const itens: readonly OpcaoDeSelecao[] =
    alvoDaBusca === ''
      ? comVazio
      : opcoes.filter((o) => normalizar(o.rotulo).includes(alvoDaBusca));

  // O INDICE DENTRO DA LISTA DESENHADA, e e ele que posiciona o cursor do teclado ao abrir.
  // Com busca ativa a escolha pode nem estar entre os resultados, e ai ele vale -1, que e o
  // certo: o cursor comeca no primeiro resultado.
  const escolhido = itens.findIndex((o) => o.valor === (value ?? ''));

  // Guarda o tamanho da lista desenhada para o efeito que mede o "tem mais" (declarado acima).
  if (quantidadeVisivel !== itens.length) setQuantidadeVisivel(itens.length);
  /**
   * O ROTULO DO GATILHO SAI DA LISTA COMPLETA, e nunca da filtrada.
   *
   * ESTE E O DEFEITO QUE A BUSCA QUASE INTRODUZIU: lendo da lista filtrada, digitar `x` no
   * campo apagaria o valor escrito no proprio gatilho, e a pessoa veria a escolha dela
   * desaparecer enquanto procurava outra. Nada foi desescolhido; so o rotulo tinha sumido.
   */
  const escolhidoNoTodo = comVazio.findIndex((o) => o.valor === (value ?? ''));
  const rotuloAtual = escolhidoNoTodo >= 0 ? comVazio[escolhidoNoTodo]!.rotulo : (vazio ?? '');

  function abrir() {
    if (disabled) return;
    // 3 · A LISTA ABRE PARA CIMA quando nao ha espaco embaixo. A altura maxima da lista e
    // 280px, e o `+ 8` e o respiro entre ela e o campo.
    const caixa = gatilho.current?.getBoundingClientRect();
    if (caixa) {
      const abaixo = window.innerHeight - caixa.bottom;
      setParaCima(abaixo < 288 && caixa.top > abaixo);
      /**
       * A ALTURA DA LISTA SAI DO ESPAÇO QUE EXISTE, e não de um número fixo.
       *
       * O DEFEITO QUE ISTO CONSERTA, apontado pelo fundador em 07/10/2026 sobre o Questionário
       * Geral no celular: *"o tamanho do sapato nao consegui ver todos para selecionar"*. São onze
       * opções, e a lista tinha teto fixo de 280px. Com o campo no meio de uma tela de celular,
       * sobram menos de 280px abaixo dele, mas o suficiente para a regra acima NÃO virar a lista
       * para cima. Ela abria para baixo e passava do fim da tela, e como o painel é `fixed`, ele
       * não rola com a página: as últimas opções ficavam inalcançáveis.
       *
       * O TETO PASSA A SER O ESPAÇO REAL do lado escolhido, menos 16px de respiro, com piso de
       * 160px. O piso existe porque uma lista de 40px seria pior que uma lista cortada: com ele,
       * cabem pelo menos três alvos de toque, e o resto rola DENTRO da lista, que é o gesto que
       * funciona.
       */
      const paraCimaAgora = abaixo < 288 && caixa.top > abaixo;
      const espacoUtil = paraCimaAgora ? caixa.top : abaixo;
      setAlturaMaxima(Math.max(160, Math.min(280, espacoUtil - 16)));
      // A MEDIDA VAI JUNTO, para o painel do portal saber onde se desenhar. Sem ela ele
      // nasceria no canto da janela, porque fora da arvore nao ha pai que o posicione.
      setCaixaDoGatilho({
        top: caixa.top,
        left: caixa.left,
        width: caixa.width,
        bottom: caixa.bottom,
      });
    }
    // 2 · A OPCAO SELECIONADA JA VEM SOB O CURSOR, e nao o primeiro item: quem abre para
    // conferir o que esta escolhido encontra a resposta sem rolar.
    setAtivo(escolhido >= 0 ? escolhido : 0);
    setAberto(true);
    /**
     * O FOCO VAI PARA A BUSCA, e nao fica no gatilho.
     *
     * Sem isto a pessoa abriria o combo e precisaria de um segundo gesto para comecar a
     * digitar, que e exatamente o gesto que a busca existe para poupar. No celular isso
     * tambem e o que faz o teclado subir sozinho.
     *
     * `requestAnimationFrame` porque o campo ainda nao existe no DOM neste instante: ele
     * nasce no render que este `setAberto` dispara.
     */
    if (comBusca) requestAnimationFrame(() => campoBusca.current?.focus());
  }

  function fechar(devolverFoco = true) {
    setAberto(false);
    setAtivo(-1);
    // A BUSCA SE LIMPA AQUI, e nao ao escolher: ver o comentario do estado. Fechar e o unico
    // momento em que limpar nao apaga nada que a pessoa ainda esteja usando.
    setBusca('');
    // 4 · O FOCO VOLTA PARA QUEM ABRIU. E o item que mais se esquece, e o que quebra o
    // teclado para quem depende dele.
    if (devolverFoco) gatilho.current?.focus();
  }

  function escolher(indice: number) {
    const opcao = itens[indice];
    if (!opcao) return;
    onChange?.({ target: { value: opcao.valor, name } });
    fechar();
  }

  // FECHAR AO CLICAR FORA. `pointerdown` e nao `click`: com `click`, um arraste que comeca
  // dentro da lista e termina fora fecharia o campo no meio do gesto.
  useEffect(() => {
    if (!aberto) return;
    function aoApontar(evento: PointerEvent) {
      const alvo = evento.target as Node;
      // O PAINEL CONTA COMO DENTRO, e esta linha e obrigatoria depois do portal: ele NAO E
      // MAIS descendente da raiz, entao clicar numa opcao seria lido como clique fora e
      // fecharia o campo ANTES de a escolha acontecer.
      if (raiz.current?.contains(alvo) || painel.current?.contains(alvo)) return;
      fechar(false);
    }
    document.addEventListener('pointerdown', aoApontar);
    return () => document.removeEventListener('pointerdown', aoApontar);
  }, [aberto]);

  // A OPCAO ATIVA FICA VISIVEL enquanto se navega com as setas. Sem isto, a lista rola
  // sozinha so quando o navegador resolve, e o cursor some da tela em lista longa.
  useEffect(() => {
    if (!aberto || ativo < 0) return;
    const alvo = lista.current?.children[ativo] as HTMLElement | undefined;
    alvo?.scrollIntoView({ block: 'nearest' });
  }, [aberto, ativo]);

  function aoTeclar(evento: React.KeyboardEvent) {
    const tecla = evento.key;

    if (!aberto) {
      // Abrir pelo teclado, do jeito que o nativo abria: seta, espaco ou Enter.
      if (tecla === 'ArrowDown' || tecla === 'ArrowUp' || tecla === 'Enter' || tecla === ' ') {
        evento.preventDefault();
        abrir();
      }
      return;
    }

    if (tecla === 'Escape') {
      evento.preventDefault();
      fechar();
      return;
    }
    if (tecla === 'Enter' || tecla === ' ') {
      evento.preventDefault();
      if (ativo >= 0) escolher(ativo);
      return;
    }
    if (tecla === 'ArrowDown') {
      evento.preventDefault();
      setAtivo((i) => (i + 1) % itens.length);
      return;
    }
    if (tecla === 'ArrowUp') {
      evento.preventDefault();
      setAtivo((i) => (i - 1 + itens.length) % itens.length);
      return;
    }
    if (tecla === 'Home') {
      evento.preventDefault();
      setAtivo(0);
      return;
    }
    if (tecla === 'End') {
      evento.preventDefault();
      setAtivo(itens.length - 1);
      return;
    }
    // DIGITAR PULA PARA A OPCAO, que e o comportamento do `<select>` nativo e o que quem
    // usa teclado espera. Uma letra so, sem acumular: acumular exigiria um temporizador.
    //
    // COM BUSCA LIGADA ISTO NAO ACONTECE, e a razao e que seria dois comportamentos
    // disputando a mesma tecla: a letra iria para o filtro E moveria o cursor, entao a lista
    // se recortaria e pularia ao mesmo tempo. Com busca, quem responde a digitacao e o campo.
    if (!comBusca && tecla.length === 1 && /\S/.test(tecla)) {
      const letra = tecla.toLowerCase();
      const achado = itens.findIndex((o) => o.rotulo.toLowerCase().startsWith(letra));
      if (achado >= 0) setAtivo(achado);
    }
  }

  /**
   * A MAIOR opcao da lista, ja traduzida. E ela que da a largura do campo, pelo medidor invisivel
   * que aparece logo antes do gatilho no JSX.
   *
   * POR QUE MEDIR POR TEXTO, e nao contar caracteres: a largura de uma letra muda com maiuscula,
   * acento e fonte, e uma conta em `ch` erra justamente nos rotulos compridos, que sao os que
   * cortam. O medidor desenha o texto de verdade, na mesma fonte e no mesmo tamanho do gatilho, e
   * por isso acerta sem calculo nenhum.
   *
   * O CUSTO PARA QUEM USA LEITOR DE TELA E ZERO: o medidor tem altura zero, e `aria-hidden`.
   */
  const maiorRotulo = [vazio ?? '', ...opcoes.map((opcao) => t(opcao.rotulo))].reduce(
    (maior, atual) => (atual.length > maior.length ? atual : maior),
    '',
  );

  /*
   * LARGURA MINIMA DE 14rem, pedida pelo fundador em 19/09/2026: "aumentar o tamanho do campo
   * Produto e Aba (so consegue enxergar o texto do combo quando seleciona e depois de selecionado
   * corta do texto)". Sem ela, numa linha compacta a peca encolhia ate o tamanho do rotulo, e
   * "Acompanhamento Anual" virava "Acompanhamento A...".
   *
   * `max-w-full` junto, e nao so o minimo: em coluna estreita (celular de 390px, painel lateral,
   * modal) um minimo sem teto empurraria a linha para fora da tela, e a regra da casa mede
   * `scrollWidth` igual a `clientWidth`.
   */
  return (
    <div className="flex min-w-[14rem] max-w-full flex-col gap-cx-1" ref={raiz}>
      <label
        htmlFor={id}
        className="text-cx-sm font-medium text-texto-suave"
        id={`${id}-rotulo`}
      >
        {t(rotulo)}
        {obrigatorio && (
          <span aria-hidden="true" className="ml-cx-1 text-erro">
            *
          </span>
        )}
      </label>

      <div className="relative">
        {/*
          O MEDIDOR INVISIVEL. Ele desenha a maior opcao com a mesma fonte e o mesmo tamanho do
          gatilho, com altura zero, e por isso o navegador ja reserva a largura necessaria ANTES
          de a pessoa escolher qualquer coisa.

          `pr-[3rem]` guarda o espaco do chevron e do respiro, que o gatilho tem e o texto nao.
          `max-w-full` no bloco de cima continua mandando: em tela estreita o campo encolhe e o
          texto escolhido usa o `truncate` do gatilho, enquanto a lista aberta mostra inteiro.
        */}
        <span
          aria-hidden="true"
          className="pointer-events-none block h-0 overflow-hidden whitespace-nowrap pl-cx-3 pr-[3rem] text-cx-base"
        >
          {maiorRotulo}
        </span>
        <button
          ref={gatilho}
          id={id}
          type="button"
          disabled={disabled}
          // `listbox` e nao `combobox`: nao ha campo de texto aqui, e anunciar combobox
          // faria o leitor de tela prometer digitacao que esta peca nao aceita.
          aria-haspopup="listbox"
          aria-expanded={aberto}
          aria-controls={aberto ? idLista : undefined}
          aria-labelledby={`${id}-rotulo ${id}`}
          aria-required={obrigatorio || undefined}
          aria-invalid={erro ? true : undefined}
          aria-describedby={[erro ? idErro : null, ajuda ? idAjuda : null]
            .filter(Boolean)
            .join(' ') || undefined}
          onClick={() => (aberto ? fechar() : abrir())}
          onKeyDown={aoTeclar}
          className={[
            // 1 · ALVO DE 44px, o minimo da Apple.
            'flex min-h-toque w-full items-center gap-cx-2 rounded-cx-md border px-cx-3 text-left text-cx-base',
            'bg-superficie text-texto',
            erro ? 'border-erro' : 'border-borda-forte',
            disabled ? 'cursor-not-allowed text-texto-fraco' : 'hover:border-marca',
            'focus:border-marca focus:outline-none focus:ring-2 focus:ring-marca/20',
          ].join(' ')}
        >
          <span className={rotuloAtual ? 'min-w-0 flex-1 truncate' : 'min-w-0 flex-1 truncate text-texto-fraco'}>
            {rotuloAtual ? t(rotuloAtual) : ''}
          </span>
          <IconeChevron
            aria-hidden="true"
            className={`h-4 w-4 shrink-0 text-texto-fraco ${aberto ? 'rotate-180' : ''}`}
          />
        </button>

        {/*
          O PAINEL VAI PARA O `body`, e nao fica aqui dentro.
          Ver `caixaDoGatilho` la em cima para o defeito que isto conserta: o cartao que
          envolve o campo tem `overflow-hidden`, e cortava 84 pixels da lista. Fora da
          arvore, nenhum `overflow` de ancestral alcanca este bloco.
        */}
        {aberto &&
          createPortal(
          /**
           * O MATERIAL SAIU DO `<ul>` E VEIO PARA ESTE `<div>`, e a mudanca e estrutural.
           *
           * Ate 13/09/2026 a lista ERA o painel. Com o campo de busca, os dois precisam
           * dividir a mesma superficie de vidro, e o campo NAO PODE ROLAR JUNTO: se ele
           * ficasse dentro do `<ul>`, sumiria assim que a pessoa rolasse a lista, no meio da
           * propria digitacao. E o mesmo desenho que a `SelecaoMultipla` ja usa.
           *
           * SEM BUSCA O RESULTADO E IDENTICO ao que era: as classes sao as mesmas, e o `<ul>`
           * so ficou com o que e dele, a altura maxima e a rolagem.
           */
          <div
            ref={painel}
            /*
             * `fixed` E NAO `absolute`, porque este bloco nao mora mais na arvore do campo:
             * ele e desenhado no `body` pelo portal logo abaixo. Em `absolute` ele se
             * posicionaria pelo `body`, que rola, e a lista descolaria do campo ao rolar.
             *
             * A POSICAO VEM DA MEDIDA DO GATILHO, e nao de CSS: fora da arvore nao ha pai
             * que a resolva.
             *
             * A LARGURA E MINIMA, E NAO EXATA, desde 19/09/2026. Ela copiava o campo
             * (`width: caixaDoGatilho.width`), e ai a lista herdava o aperto dele: o fundador
             * abriu o combo de produto e leu "Acompan...", "Analise d...", "Coloraca...". Agora o
             * painel comeca na largura do campo e CRESCE ate caber a maior opcao (`w-max` na
             * classe), com teto no espaco que sobra ate a borda da janela, menos 16px de respiro.
             *
             * O TETO E MEDIDO, e nao um numero bonito: sem ele, uma opcao longa empurraria o
             * painel para fora da tela, e a lista ficaria cortada do outro lado, que e o mesmo
             * defeito com outro nome.
             */
            style={
              caixaDoGatilho
                ? {
                    position: 'fixed',
                    left: caixaDoGatilho.left,
                    minWidth: caixaDoGatilho.width,
                    maxWidth: Math.max(caixaDoGatilho.width, window.innerWidth - caixaDoGatilho.left - 16),
                    // O respiro de 8px entre o campo e a lista, nos dois sentidos.
                    ...(paraCima
                      ? { bottom: window.innerHeight - caixaDoGatilho.top + 8 }
                      : { top: caixaDoGatilho.bottom + 8 }),
                  }
                : undefined
            }
            className={[
              // O MATERIAL: vidro GROSSO, porque esta peca esta por cima de tudo. Vidro
              // sobre vidro nao empilha, e quem esta por cima fica grosso. ADR 0052.
              // `w-max`: a largura sai do CONTEUDO, entre o minimo e o teto calculados no `style`
              // acima. Sem ela, `minWidth` sozinho nunca passaria da largura do campo.
              'relative w-max z-painel overflow-hidden rounded-cx-md border border-vidro',
              'bg-vidro-grosso backdrop-blur-vidro backdrop-saturate-vidro shadow-cx-lg',
            ].join(' ')}
          >
            {comBusca && (
              /* `type=text` e nao `search`: o `search` desenha um X nativo que segue o tema
                 do SISTEMA OPERACIONAL, e nao o da pagina, e a peca perderia o tema escuro
                 num pedaco de si mesma. Mesma decisao da `SelecaoMultipla`. */
              <div className="border-b border-vidro p-cx-2">
                <input
                  ref={campoBusca}
                  type="text"
                  value={busca}
                  onChange={(e) => {
                    setBusca(e.target.value);
                    // O CURSOR VOLTA PARA O PRIMEIRO RESULTADO a cada tecla. Sem isto ele
                    // ficaria apontando um indice da lista anterior, e o Enter escolheria uma
                    // opcao que a pessoa nao esta vendo mais.
                    setAtivo(0);
                  }}
                  // O MESMO teclado do gatilho: seta, Enter, Escape e Home continuam valendo
                  // com o foco aqui dentro, senao a busca quebraria a navegacao por teclado.
                  onKeyDown={aoTeclar}
                  placeholder={t('Buscar')}
                  aria-label={t('Buscar na lista')}
                  aria-controls={idLista}
                  aria-activedescendant={ativo >= 0 ? `${id}-op-${ativo}` : undefined}
                  aria-autocomplete="list"
                  role="combobox"
                  aria-expanded={aberto}
                  className={[
                    'min-h-toque w-full rounded-cx-sm border border-borda bg-superficie px-cx-2 text-cx-sm text-texto',
                    'focus:border-marca focus:outline-none focus:ring-2 focus:ring-marca/20',
                  ].join(' ')}
                />
              </div>
            )}
            {/* NADA ENCONTRADO E UM ESTADO, e nao uma lista vazia. Lista que fica em branco
                parece defeito de carregamento, e a regra dura da casa e que ausencia se diz.
                Ele so aparece com busca ligada: sem ela, lista vazia significa que quem
                chamou a peca nao passou opcao nenhuma, que e outro problema e nao e daqui. */}
            {comBusca && itens.length === 0 && (
              <p className="px-cx-3 py-cx-3 text-cx-sm text-texto-fraco">
                {t('Nada encontrado')}
              </p>
            )}
          <ul
            ref={lista}
            id={idLista}
            role="listbox"
            aria-labelledby={`${id}-rotulo`}
            aria-activedescendant={ativo >= 0 ? `${id}-op-${ativo}` : undefined}
            tabIndex={-1}
            onScroll={medirSeHaMais}
            // O teto vem medido do espaço que existe na tela (ver `abrir()`), e não de uma classe
            // fixa: era o teto fixo que encurtava a lista sem necessidade no celular.
            style={{ maxHeight: alturaMaxima }}
            /**
             * A BARRA DE ROLAGEM FICA VISÍVEL, e isto é a metade do conserto de 07/10/2026.
             *
             * MEDIDO na tela de 390px: das doze opções do tamanho de sapato, SEIS apareciam
             * inteiras, uma ficava cortada na borda, e a barra de rolagem tinha largura ZERO. Sem
             * barra e sem outro sinal, a lista parecia terminar no 38, e foi o que o fundador
             * relatou: *"nao consegui ver todos para selecionar"*.
             *
             * `scrollbar-width: thin` devolve a barra sem ela roubar largura do texto. Em aparelho
             * de toque a barra só aparece durante o gesto, e é por isso que ela sozinha não basta:
             * o degradê do rodapé (logo abaixo) é o sinal que fica parado na tela.
             */
            className="overflow-y-auto [scrollbar-width:thin]"
          >
            {itens.map((opcao, indice) => {
              const marcado = opcao.valor === (value ?? '');
              return (
                <li
                  key={opcao.valor || '__vazio'}
                  id={`${id}-op-${indice}`}
                  role="option"
                  aria-selected={marcado}
                  onPointerDown={(e) => {
                    // `preventDefault` para o botao nao perder o foco antes do clique
                    // registrar: sem isto, o `pointerdown` de fechar dispara primeiro.
                    e.preventDefault();
                    escolher(indice);
                  }}
                  onPointerEnter={() => setAtivo(indice)}
                  className={[
                    'flex min-h-toque cursor-pointer items-center gap-cx-2 px-cx-3 text-cx-base',
                    // TINTA E NAO FUNDO CHEIO, pelo padrao de vidro de 09/09/2026. Este e o
                    // cursor do TECLADO percorrendo a lista, e nao a escolha: um azul chapado
                    // correndo linha a linha enquanto a pessoa segura a seta e o tipo de peso
                    // visual que o padrao da casa reserva para a acao principal.
                    //
                    // SEM BORDA AQUI, de proposito, e a razao e geometrica: borda numa linha
                    // de lista cresce 2px so na linha ativa, e a lista inteira daria um pulo a
                    // cada tecla. Quem separa esta linha das outras e o contraste do texto.
                    // O ROTULO NAO TROCA DE COR, e isto foi medido: `text-marca` sobre
                    // `bg-marca/15` da 4.28 no tema claro, abaixo do piso de 4.5. Alem disso o
                    // que esta linha significa e ONDE O CURSOR ESTA, e nao o que foi escolhido:
                    // a tinta marca a posicao, o simbolo de visto marca a escolha, e o rotulo
                    // continua com o mesmo peso de leitura das outras linhas.
                    indice === ativo ? 'bg-marca/15 text-texto dark:bg-marca/25' : 'text-texto',
                  ].join(' ')}
                >
                  {/*
                    SEM `truncate` desde 19/09/2026, por pedido do fundador: "todo combo tem que
                    ter uma inteligencia para nao cortar o texto". O painel agora cresce ate caber
                    (ver `w-max` acima), e no caso extremo em que o teto da janela morde a largura
                    a opcao QUEBRA EM DUAS LINHAS em vez de sumir com reticencias. Nome cortado e
                    escolha as cegas; nome em duas linhas continua legivel.
                  */}
                  <span className="min-w-0 flex-1 break-words">{t(opcao.rotulo)}</span>
                  {/* A MARCA DE ESCOLHIDO E UM SIMBOLO, e nao so a cor de fundo: cor
                      sozinha nao existe para quem nao a distingue, e o fundo aqui ja
                      significa outra coisa (o cursor do teclado). */}
                  {marcado && <IconeVisto aria-hidden="true" className="h-4 w-4 shrink-0 text-marca" />}
                </li>
              );
            })}
          </ul>
          {/*
            O DEGRADÊ QUE DIZ "TEM MAIS AQUI EMBAIXO".

            É a outra metade do conserto de 07/10/2026. A barra de rolagem resolve no computador,
            e em aparelho de toque ela só aparece DURANTE o gesto: quem ainda não arrastou não vê
            barra nenhuma, e foi assim que as opções de 39 a 43 do tamanho de sapato deixaram de
            existir para quem olhou a tela.

            Ele SOME ao chegar no fim, porque sinal que não some vira enfeite e para de ser lido.
            `pointer-events-none` para ele não roubar o toque da opção que está embaixo, e
            `aria-hidden` porque quem usa leitor de tela já ouve a contagem de itens da lista.
          */}
          {haMais && (
            <div
              aria-hidden="true"
              data-testid="selecao-tem-mais"
              className="pointer-events-none absolute inset-x-0 bottom-0 h-cx-6 rounded-b-cx-md bg-gradient-to-t from-vidro-grosso to-transparent"
            />
          )}
          </div>,
            document.body,
          )}
      </div>

      {ajuda && !erro && (
        <p id={idAjuda} className="text-cx-xs text-texto-fraco">
          {t(ajuda)}
        </p>
      )}
      {erro && (
        <p id={idErro} role="alert" className="text-cx-xs font-medium text-erro">
          {t(erro)}
        </p>
      )}
    </div>
  );
}
